import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useWorkspaces } from '@/hooks';
import { useAuthStore } from '@/stores/authStore';
import { workspaceApi } from '@/lib/api';
import { Workspace, WorkspaceRole } from '@/types';

interface WorkspaceContextType {
  activeWorkspace: Workspace | null;
  workspaces: Workspace[];
  setActiveWorkspaceId: (id: string) => void;
  /** Open a workspace as a platform admin without being a member. The
   *  workspace is fetched separately and merged into the list with a
   *  synthetic OWNER role; backend `verifyMembership` lets admins through.
   *  No-op for non-admin users. */
  setActiveWorkspaceAsAdmin: (id: string) => void;
  /** Drop the admin impersonation and snap back to the user's first own
   *  workspace. */
  stopImpersonating: () => void;
  /** True when an admin has opened a customer workspace and is currently
   *  viewing the dashboard through their lens. Drives the warning banner. */
  isImpersonating: boolean;
  isLoading: boolean;
}

const WorkspaceContext = createContext<WorkspaceContextType>({
  activeWorkspace: null,
  workspaces: [],
  setActiveWorkspaceId: () => {},
  setActiveWorkspaceAsAdmin: () => {},
  stopImpersonating: () => {},
  isImpersonating: false,
  isLoading: true,
});

export const useWorkspaceContext = () => useContext(WorkspaceContext);

/**
 * RBAC convenience hook — returns the authenticated user's role for the
 * currently active workspace, or null if no workspace is loaded yet.
 *
 * Components doing UI gating should use the boolean helpers below
 * (`useIsOwner` / `useIsMember`) rather than comparing the role string
 * directly, to avoid string-typo bugs and to keep all role checks routed
 * through one place.
 */
export const useActiveWorkspaceRole = (): WorkspaceRole | null => {
  const { activeWorkspace } = useContext(WorkspaceContext);
  return activeWorkspace?.role ?? null;
};

/**
 * True when the authenticated user is an OWNER of the active workspace.
 * Returns false while the workspace is still loading — UI gates should
 * default to "hidden / disabled" until we know for sure.
 */
export const useIsOwner = (): boolean => {
  const role = useActiveWorkspaceRole();
  return role === WorkspaceRole.OWNER;
};

/**
 * V131 — true when the user can approve / reject posts. OWNER + APPROVER.
 * Used to gate the approve / reject action surfaces (review inbox buttons,
 * approve from PostCard, etc.) so MEMBERs see read-only review.
 */
export const useIsApproverOrOwner = (): boolean => {
  const role = useActiveWorkspaceRole();
  return role === WorkspaceRole.OWNER || role === WorkspaceRole.APPROVER;
};

/**
 * True when the authenticated user is any kind of member (OWNER or MEMBER)
 * of the active workspace. Mostly used for type-narrowing.
 */
export const useIsMember = (): boolean => {
  const role = useActiveWorkspaceRole();
  return role !== null;
};

// Canonical localStorage key for the active workspace. The old
// `activeWorkspaceId` key is migrated transparently on first read.
const STORAGE_KEY = 'mf_active_workspace';
const LEGACY_KEY = 'activeWorkspaceId';
// Session-scoped — admin impersonation should not survive a tab close.
const ADMIN_IMPERSONATE_KEY = 'mf_admin_impersonating_ws';

function readImpersonationId(): string | null {
  if (typeof window === 'undefined') return null;
  return sessionStorage.getItem(ADMIN_IMPERSONATE_KEY);
}

export default function WorkspaceProvider({ children }: { children: React.ReactNode }) {
  const { data: ownWorkspaces = [], isLoading, isFetching } = useWorkspaces();
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.systemRole === 'admin';
  // Hydrate synchronously via the `useState` initializer so the very first
  // render already has the stored workspace id. Previously this lived in a
  // `useEffect`, which left `activeId === null` on the initial render —
  // the validation effect below then fired in the SAME commit, saw
  // "no active id, fall back to workspaces[0]", and silently switched
  // every freshly-mounted layout (DashboardLayout → AdminLayout transition)
  // to whichever workspace happened to be first in the list. Niels hit
  // this navigating from Ludicrous → /admin/settings/ai and landing on
  // Callido. Reading + migrating the legacy key in the initializer keeps
  // the previous migration semantics without the effect ordering race.
  const [activeId, setActiveId] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    const stored = localStorage.getItem(STORAGE_KEY) ?? localStorage.getItem(LEGACY_KEY);
    if (stored && localStorage.getItem(LEGACY_KEY) && !localStorage.getItem(STORAGE_KEY)) {
      localStorage.setItem(STORAGE_KEY, stored);
      localStorage.removeItem(LEGACY_KEY);
    }
    return stored;
  });
  const [impersonateId, setImpersonateId] = useState<string | null>(readImpersonationId);

  // Fetch the impersonated workspace separately - it's not in the user's
  // memberships, so useWorkspaces() won't return it. Backend
  // `verifyMembership` bypasses for system admins so this 200s.
  const { data: impersonatedWorkspace } = useQuery({
    queryKey: ['impersonated-workspace', impersonateId],
    queryFn: () => workspaceApi.get(impersonateId!),
    enabled: isAdmin && !!impersonateId,
    staleTime: 5 * 60_000,
  });

  // Merge impersonated workspace into the list with a synthetic OWNER role
  // (matches the backend bypass - admin sees the full owner view incl.
  // plan editor, invitations, SMTP) and the isImpersonated flag so the
  // banner / switcher can light up.
  const workspaces = useMemo<Workspace[]>(() => {
    if (!impersonatedWorkspace) return ownWorkspaces;
    if (ownWorkspaces.some((w) => w.id === impersonatedWorkspace.id)) return ownWorkspaces;
    return [
      ...ownWorkspaces,
      { ...impersonatedWorkspace, role: WorkspaceRole.OWNER, isImpersonated: true },
    ];
  }, [ownWorkspaces, impersonatedWorkspace]);

  // Validate the stored ID against the fetched list. If the user was
  // removed from a workspace (or the stored ID is stale), fall back to the
  // first workspace they still belong to.
  //
  // CRITICAL: only validate when React Query is NOT in the middle of a
  // refetch. If we validate while `isFetching === true`, we may be looking
  // at the previous render's stale `data` array — for example right after
  // the user accepts an invite and AcceptInvitePage invalidates the
  // workspaces query before navigating to /dashboard. The stale array
  // doesn't contain the just-joined workspace, so the validation
  // incorrectly falls back and OVERWRITES both `activeId` and localStorage,
  // permanently dropping the user back into their old workspace.
  //
  // Also skip when impersonating but the impersonated workspace hasn't
  // landed yet — otherwise we'd snap the admin back to their own ws on
  // the brief race between mount and the workspaceApi.get response.
  useEffect(() => {
    if (isFetching) return;
    if (workspaces.length === 0) return;
    if (impersonateId && !impersonatedWorkspace) return;
    const stillMember = activeId && workspaces.some((w) => w.id === activeId);
    if (!stillMember) {
      const fallback = workspaces[0].id;
      setActiveId(fallback);
      localStorage.setItem(STORAGE_KEY, fallback);
    }
  }, [workspaces, activeId, isFetching, impersonateId, impersonatedWorkspace]);

  const setActiveWorkspaceId = (id: string) => {
    setActiveId(id);
    localStorage.setItem(STORAGE_KEY, id);
    // Switching to a workspace via the regular switcher implicitly cancels
    // any active impersonation - the admin is back in their own ws.
    if (impersonateId && id !== impersonateId) {
      sessionStorage.removeItem(ADMIN_IMPERSONATE_KEY);
      setImpersonateId(null);
    }
  };

  const setActiveWorkspaceAsAdmin = (id: string) => {
    if (!isAdmin) return;
    sessionStorage.setItem(ADMIN_IMPERSONATE_KEY, id);
    setImpersonateId(id);
    setActiveId(id);
    localStorage.setItem(STORAGE_KEY, id);
  };

  const stopImpersonating = () => {
    sessionStorage.removeItem(ADMIN_IMPERSONATE_KEY);
    setImpersonateId(null);
    if (ownWorkspaces.length > 0) {
      const fallback = ownWorkspaces[0].id;
      setActiveId(fallback);
      localStorage.setItem(STORAGE_KEY, fallback);
    }
  };

  const activeWorkspace = useMemo(
    () => workspaces.find((w) => w.id === activeId) ?? null,
    [workspaces, activeId],
  );

  const isImpersonating = !!impersonateId && activeId === impersonateId;

  return (
    <WorkspaceContext.Provider
      value={{
        activeWorkspace,
        workspaces,
        setActiveWorkspaceId,
        setActiveWorkspaceAsAdmin,
        stopImpersonating,
        isImpersonating,
        isLoading,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
}
